/**
 * Crypto Clicker 게임 상태 정의
 * 순수 데이터만 포함 - window, document, PIXI 절대 금지
 */

import { GENERATORS, getMilestoneMultiplier } from './config/generators.js';
import { ACHIEVEMENTS } from './config/achievements.js';
import { getUpgradeMultiplier, getGlobalCPSMultiplier, getClickPowerMultiplier } from './config/upgrades.js';
import { MARKET_CONFIG } from './config/market.js';
import { DARKPOOL_CONFIG } from './config/darkpool.js';

/**
 * 생산기 인스턴스
 */
export interface Generator {
  id: string;
  count: number;
}

/**
 * 업적 진행 상태
 */
export interface Achievement {
  id: string;
  unlocked: boolean;
  claimedAt?: number;
}

/**
 * 골든 이벤트 상태
 */
export interface GoldenEventState {
  active: boolean;
  type: string | null;
  spawnedAt: number;
  expiresAt: number;
  nextSpawnTime: number;
}

/**
 * 골든 효과 (진행 중인 버프)
 */
export interface ActiveGoldenEffect {
  type: string;
  multiplier: number;
  expiresAt: number;
}

/**
 * 콤보 시스템
 */
export interface ComboState {
  count: number;
  lastClickTime: number;
  maxCombo: number;
  decayTime: number; // 3000ms
}

/**
 * 클릭 부스터 (광고 기반)
 */
export interface ClickBoosterState {
  active: boolean;
  expiresAt: number;
  cooldownUntil: number;
}

/**
 * 일일 보너스
 */
export interface DailyBonusState {
  lastClaimDate: string; // YYYY-MM-DD
  streak: number;
  canClaim: boolean;
}

/**
 * 오프라인 보너스
 */
export interface OfflineBonusState {
  pending: boolean;
  earnings: number;
  duration: number;
  capped: boolean;
}

/**
 * 환생 상태 (1차)
 */
export interface PrestigeState {
  count: number;
  blockchain: number;
  totalBlockchainEarned: number;
  buffs: Record<string, number>; // buffId -> level
}

/**
 * 2차 환생 상태 (메타버스 코인)
 */
export interface Prestige2State {
  unlocked: boolean;
  count: number;
  metaCoins: number;
  totalMetaCoinsEarned: number;
  purchasedUpgrades: string[]; // upgrade IDs
}

/**
 * 통계
 */
export interface Statistics {
  totalEarned: number;
  totalClicks: number;
  totalGoldenClicks: number;
  luckyGoldenCount: number;
  adsWatched: number;
  offlineClaims: number;
  dogeClicks: number;
  playTime: number; // 초
  firstPlayTime: number; // 최초 플레이 타임스탬프
}


/**
 * 다크풀 경매 시스템
 */
export type DarkPoolPhase = 'idle' | 'betting' | 'reveal' | 'result';

export interface DarkPoolCoin {
  id: string;
  name: string;
  icon: string;
}

export interface DarkPoolBet {
  coinId: string;
  amount: number;
}

export interface DarkPoolInsiderInfo {
  coinId: string;
  hintMultiplier: number;
}

export interface DarkPoolResult {
  winnerId: string;
  multiplier: number;
}

export interface DarkPoolState {
  phase: DarkPoolPhase;
  coins: DarkPoolCoin[];
  playerBet: DarkPoolBet | null;
  insiderInfo: DarkPoolInsiderInfo | null;
  result: DarkPoolResult | null;
  // Pre-determined outcome (hidden from player)
  _outcome: DarkPoolResult | null;
  phaseEndTime: number;   // when current phase ends
  nextRoundTime: number;  // when next round starts (during idle)
  totalBets: number;      // stats: total bets placed
  totalWins: number;      // stats: total wins
  totalProfit: number;    // stats: net profit (in real coins)
  // Tutorial
  tutorialSeen: boolean;  // has player seen the dark pool tutorial
}

/**
 * 마켓 센티먼트 시스템
 */
export interface MarketState {
  price: number;
  momentum: number;
  priceHistory: number[];
  earningsMultiplier: number;

  // News
  activeNews: { id: string; expiresAt: number } | null;
  nextNewsTime: number;

  // Player manipulation
  activeManipulation: { id: string; expiresAt: number; impact: number } | null;
  manipulationCooldowns: Record<string, number>; // id -> cooldownUntil timestamp

  // Special events (crash/boom)
  specialEvent: { type: 'crash' | 'boom'; expiresAt: number } | null;

  // Tick tracking
  lastTickTime: number;
}

/**
 * 메인 게임 상태
 */
export interface GameState {
  // === 자원 ===
  coins: number;
  totalEarnedThisRun: number; // 현재 run에서 획득한 총 코인 (환생용)

  // === 생산기 (12종) ===
  generators: Record<string, Generator>;

  // === 클릭 시스템 ===
  clickPower: number;
  combo: ComboState;

  // === 골든 이벤트 ===
  goldenEvent: GoldenEventState;
  activeGoldenEffects: ActiveGoldenEffect[];

  // === 부스터/보너스 ===
  clickBooster: ClickBoosterState;
  dailyBonus: DailyBonusState;
  offlineBonus: OfflineBonusState;

  // === 환생 시스템 ===
  prestige: PrestigeState;
  prestige2: Prestige2State;

  // === 업그레이드 ===
  purchasedUpgrades: string[];

  // === 업적 ===
  achievements: Record<string, Achievement>;

  // === 통계 ===
  statistics: Statistics;

  // === 마켓 센티먼트 ===
  market: MarketState;

  // === 다크풀 ===
  darkPool: DarkPoolState;

  // === 광고 부스트 ===
  adBoostCooldownUntil: number;

  // === 메타 ===
  lastSaveTimestamp: number;
  isRunning: boolean;
}

/**
 * 초기 상태 팩토리
 */
export function createInitialState(): GameState {
  // 생산기 초기화
  const generators: Record<string, Generator> = {};
  for (const gen of GENERATORS) {
    generators[gen.id] = {
      id: gen.id,
      count: 0
    };
  }

  // 업적 초기화
  const achievements: Record<string, Achievement> = {};
  for (const ach of ACHIEVEMENTS) {
    achievements[ach.id] = {
      id: ach.id,
      unlocked: false
    };
  }

  return {
    coins: 0,
    totalEarnedThisRun: 0,

    generators,

    clickPower: 1,
    combo: {
      count: 0,
      lastClickTime: 0,
      maxCombo: 0,
      decayTime: 3000
    },

    goldenEvent: {
      active: false,
      type: null,
      spawnedAt: 0,
      expiresAt: 0,
      nextSpawnTime: Date.now() + 5 * 60 * 1000 // 첫 골든은 5분 후
    },
    activeGoldenEffects: [],

    clickBooster: {
      active: false,
      expiresAt: 0,
      cooldownUntil: 0
    },

    dailyBonus: {
      lastClaimDate: '',
      streak: 0,
      canClaim: true
    },

    offlineBonus: {
      pending: false,
      earnings: 0,
      duration: 0,
      capped: false
    },

    prestige: {
      count: 0,
      blockchain: 0,
      totalBlockchainEarned: 0,
      buffs: {}
    },

    prestige2: {
      unlocked: false,
      count: 0,
      metaCoins: 0,
      totalMetaCoinsEarned: 0,
      purchasedUpgrades: []
    },

    purchasedUpgrades: [],

    achievements,

    statistics: {
      totalEarned: 0,
      totalClicks: 0,
      totalGoldenClicks: 0,
      luckyGoldenCount: 0,
      adsWatched: 0,
      offlineClaims: 0,
      dogeClicks: 0,
      playTime: 0,
      firstPlayTime: Date.now()
    },

    market: createInitialMarketState(),

    darkPool: createInitialDarkPoolState(),

    adBoostCooldownUntil: 0,

    lastSaveTimestamp: Date.now(),
    isRunning: true
  };
}

export function createInitialDarkPoolState(): DarkPoolState {
  return {
    phase: 'idle',
    coins: [],
    playerBet: null,
    insiderInfo: null,
    result: null,
    _outcome: null,
    phaseEndTime: 0,
    nextRoundTime: Date.now() + DARKPOOL_CONFIG.roundCooldown,
    totalBets: 0,
    totalWins: 0,
    totalProfit: 0,
    tutorialSeen: false,
  };
}

export function createInitialMarketState(): MarketState {
  return {
    price: MARKET_CONFIG.startPrice,
    momentum: 0,
    priceHistory: [MARKET_CONFIG.startPrice],
    earningsMultiplier: 1.0,
    activeNews: null,
    nextNewsTime: Date.now() + MARKET_CONFIG.newsMinInterval,
    activeManipulation: null,
    manipulationCooldowns: {},
    specialEvent: null,
    lastTickTime: Date.now(),
  };
}


/**
 * 현재 CPS 계산 (모든 배율 포함)
 */
export function getCurrentCPS(state: GameState): number {
  let totalCPS = 0;

  // 생산기별 CPS
  for (const genConfig of GENERATORS) {
    const gen = state.generators[genConfig.id];
    if (!gen || gen.count === 0) continue;

    const milestoneMultiplier = getMilestoneMultiplier(gen.count);
    const upgradeMultiplier = getUpgradeMultiplier(genConfig.id, state.purchasedUpgrades);
    const baseProduction = genConfig.baseProduction * gen.count * milestoneMultiplier * upgradeMultiplier;

    totalCPS += baseProduction;
  }

  // 업그레이드 글로벌 CPS 배율
  const ownedTypes = GENERATORS.filter(g => (state.generators[g.id]?.count || 0) > 0).length;
  totalCPS *= getGlobalCPSMultiplier(state.purchasedUpgrades, ownedTypes);

  // 블록체인 배율
  const blockchainMult = 1 + state.prestige.blockchain * 0.01;
  totalCPS *= blockchainMult;

  // 환생 버프 배율
  const cpsBuffLevel = state.prestige.buffs['buff_cps'] || 0;
  const cpsBuffMult = 1 + cpsBuffLevel * 0.05;
  totalCPS *= cpsBuffMult;

  // 메타버스 업그레이드 (환생 보너스 증폭: 1% -> 2%)
  if (state.prestige2.purchasedUpgrades.includes('meta_prestige_amplify')) {
    const additionalMult = 1 + state.prestige.blockchain * 0.01; // 또 1% 추가
    totalCPS *= additionalMult;
  }

  // 메타버스 업그레이드 (마일스톤 부스트: x2 -> x3)
  if (state.prestige2.purchasedUpgrades.includes('meta_milestone_boost')) {
    totalCPS *= 1.5; // x2 -> x3 = 1.5배
  }

  // 골든 효과 (럭키 골든, 생산 부스트, 광고 x5)
  for (const effect of state.activeGoldenEffects) {
    if (effect.type === 'lucky_golden' || effect.type === 'production_boost' || effect.type === 'ad_income_x5') {
      totalCPS *= effect.multiplier;
    }
  }

  // 업적 보상 (CPS +%)
  for (const achId in state.achievements) {
    const ach = state.achievements[achId];
    if (ach.unlocked) {
      const achConfig = ACHIEVEMENTS.find(a => a.id === achId);
      if (achConfig && achConfig.reward.type === 'cps') {
        totalCPS *= 1 + achConfig.reward.value / 100;
      }
    }
  }

  // 마켓 센티먼트 배율
  if (state.market) {
    totalCPS *= state.market.earningsMultiplier;
  }

  return totalCPS;
}

/**
 * 현재 탭 파워 계산 (모든 배율 포함)
 */
export function getCurrentClickPower(state: GameState): number {
  let power = state.clickPower;

  // 업그레이드 클릭파워 배율
  power *= getClickPowerMultiplier(state.purchasedUpgrades);

  // 블록체인 배율
  const blockchainMult = 1 + state.prestige.blockchain * 0.01;
  power *= blockchainMult;

  // 환생 버프 배율
  const clickBuffLevel = state.prestige.buffs['buff_click'] || 0;
  const clickBuffMult = 1 + clickBuffLevel * 0.05;
  power *= clickBuffMult;

  // 메타버스 업그레이드 (환생 보너스 증폭)
  if (state.prestige2.purchasedUpgrades.includes('meta_prestige_amplify')) {
    const additionalMult = 1 + state.prestige.blockchain * 0.01;
    power *= additionalMult;
  }

  // 콤보 배율 (콤보 브레이커 업그레이드: 최대 3배)
  const comboMaxMult = state.purchasedUpgrades.includes('up_res_combo_max') ? 3.0 : 2.0;
  const comboMult = Math.min(1 + state.combo.count * 0.01, comboMaxMult);
  power *= comboMult;

  // 클릭 부스터 (광고)
  if (state.clickBooster.active && Date.now() < state.clickBooster.expiresAt) {
    power *= 20;
  }

  // 골든 효과 (클릭 프렌지, 광고 x5)
  for (const effect of state.activeGoldenEffects) {
    if (effect.type === 'click_frenzy' || effect.type === 'ad_income_x5' || effect.type === 'ad_tap_x10') {
      power *= effect.multiplier;
    }
  }

  // 업적 보상 (클릭 파워 +%)
  for (const achId in state.achievements) {
    const ach = state.achievements[achId];
    if (ach.unlocked) {
      const achConfig = ACHIEVEMENTS.find(a => a.id === achId);
      if (achConfig && achConfig.reward.type === 'clickPower') {
        power *= 1 + achConfig.reward.value / 100;
      }
    }
  }

  // 마켓 센티먼트 배율
  if (state.market) {
    power *= state.market.earningsMultiplier;
  }

  return power;
}
